import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Clock, CheckCircle2, AlertTriangle, ChevronRight, Loader2, User, Sparkles, Mic, Square, Volume2, RefreshCw } from "lucide-react";
import api, { createAbortSignal } from "../../api";
import SecurityHardStop from "../../components/SecurityHardStop";

// Parse a security-hold id from a 423 detail string (see SalesPitchAttempt).
const extractHoldId = (detail) => {
  const m = /SECURITY_HOLD:([0-9a-fA-F-]{36})/.exec(String(detail || ""));
  return m ? m[1] : null;
};
import SalesCertProctorHud from "../../components/SalesCertProctorHud";
import MicCheckGate from "../../components/MicCheckGate";
import { IntegrityBanner, IntegrityWatermark } from "../../components/IntegrityOverlay";
import { nextSalescertSection, isAttemptExhaustedError } from "../../utils/salescertNav";
import { trackClick } from "../../hooks/useActivityLogger";

/** VI Sales Cert · Section 3 — 5-min interactive AI Customer Role Play (text).
 *  Fresh lean variant · max 8 candidate turns · Claude-scored on submit. */
export default function SalesCertRolePlay() {
  const nav = useNavigate();
  const [phase, setPhase] = useState("loading"); // loading|briefing|mic_check|active|scoring|scored|error
  const [scenario, setScenario] = useState(null);
  const [session, setSession] = useState(null);
  // sessionRef mirrors `session` so async callbacks (mr.onstop, setInterval,
  // visibilitychange handlers) can always read the LATEST session id + messages
  // without relying on a stale render-time closure. This matters most on iOS
  // Safari where deferred callbacks after a long recording can otherwise see
  // the initial (opening-only) session and skip the reconcile.
  const sessionRef = useRef(null);
  useEffect(() => { sessionRef.current = session; }, [session]);
  // reconcilerRef holds the latest closure of reconcileSessionState so
  // interval / visibilitychange / audio-ended handlers can invoke the
  // freshest version without needing to be re-bound each render.
  const reconcilerRef = useRef(() => {});
  const [draft, setDraft] = useState("");
  const [remaining, setRemaining] = useState(0);
  const [thinking, setThinking] = useState(false);
  // iter48 — gate the 5-minute countdown on the opening-audio lifecycle.
  // The AI customer's opening line is TTS-generated asynchronously, so there's
  // a 10-12 s window between phase=active mounting and the audio actually
  // playing. We don't want the timer to burn during that silence; it should
  // only start ticking once the candidate's turn actually begins (opening
  // audio fully played, OR we gave up waiting for TTS after a fallback grace).
  const [openingAudioFinished, setOpeningAudioFinished] = useState(false);
  // iter56 — audio-synced AI text reveal. While an assistant turn is being
  // "spoken", we hide the message content and progressively reveal it in
  // sync with `audioPlayerRef.current.currentTime / duration`. The message
  // itself is already in `session.messages` with the full text; the render
  // slices it based on `aiReveal.chars` so there's no risk of losing the
  // text if the component re-mounts mid-reveal.
  // Shape: null | { turn: number, text: string, chars: number }
  const [aiReveal, setAiReveal] = useState(null);
  // iter60 — mirror `aiReveal` into a ref so the reconciler's setSession
  // closure (which runs with stale captured state) can still check whether
  // a message's reveal is in flight before stamping it as completed. The
  // reconciler was the final flash source: it merged the server's full
  // message list AND stamped every assistant turn as completed in the
  // same render, bypassing the yet-to-arrive `presentAiTurn` reveal.
  const aiRevealRef = useRef(null);
  useEffect(() => { aiRevealRef.current = aiReveal; }, [aiReveal]);
  // iter59 — assistant messages are NEVER shown until their reveal starts.
  // The flash of full text in iter58 was caused by rendering messages that
  // exist in `session.messages` before `aiReveal` has been seeded for them
  // (opening between mount and audio-arrival, turn messages added by the
  // reconciler before `presentAiTurn` can seed the reveal, etc.). We now
  // gate assistant-bubble rendering strictly on two states:
  //   • reveal is CURRENTLY active for that turn (slice + caret), OR
  //   • reveal has COMPLETED for that turn (full text, no caret).
  // Anything else → the bubble is not rendered at all. User messages are
  // always rendered as before. `completedReveals` is Set-shaped but stored
  // as state so stamping triggers a re-render for the "reveal done" case.
  const [completedReveals, setCompletedReveals] = useState(() => new Set());
  const markRevealComplete = (turn) => {
    if (turn === null || turn === undefined) return;
    setCompletedReveals((prev) => {
      if (prev.has(turn)) return prev;
      const next = new Set(prev);
      next.add(turn);
      return next;
    });
  };
  // iter58 — audio-dedupe guard. The reconciler (triggered by onEnded /
  // visibilitychange / periodic poll) can call `playAudio` for the SAME
  // assistant turn that `presentAiTurn` is already handling, which
  // re-sources the <audio> element mid-playback and makes the first
  // couple of words audibly repeat. We stamp each turn here when its
  // audio is handed to `playAudio` and refuse to replay the same turn.
  const playedTurnsRef = useRef(new Set());
  // iter63 — turns whose audio fetch is already owned by `presentAiTurn`. The
  // reconciler used to start a SECOND parallel fetchTurnAudio loop for the same
  // turn (2x polling + 2x ~100-300 KB downloads, which hurts most on slow links).
  const presentingTurnsRef = useRef(new Set());
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [holdId, setHoldId] = useState(null);
  // Safeguard: confirm voice enrolment via /salescert/status before entering
  // the room. Belt (mic-check endpoint now enrols synchronously) and braces
  // (this poll) — if the enrolment isn't persisted, we make the candidate
  // redo the mic-check instead of letting them talk to the AI customer only
  // to have their submission rejected by _verify_voice_or_reject.
  const [enrolCheck, setEnrolCheck] = useState({ inFlight: false, error: "" });
  const timerRef = useRef(null);
  const submittedRef = useRef(false);
  const scrollRef = useRef(null);
  const mediaRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  // On iOS/Chrome-iOS, the very first .play() must be inside a user gesture
  // (the "Tap here to hear the customer" button). Once that first play
  // succeeds on a given <audio> element, the browser marks that element as
  // "unlocked" — subsequent programmatic .play() calls WILL work without
  // another user gesture. So we track unlock state and skip the tap-to-hear
  // overlay on every subsequent AI turn.
  const audioUnlockedRef = useRef(false);
  const [recording, setRecording] = useState(false);
  const recordingRef = useRef(false);   // mirror of recording for sync reads (avoids state-batching race in RAF loop)
  useEffect(() => { recordingRef.current = recording; }, [recording]);
  const [audioPlaying, setAudioPlaying] = useState(false);
  // Voice-Activity Detection state:
  //   "off"        — VAD disabled, candidate must click Speak/Stop manually
  //   "listening"  — mic open, waiting for the candidate to start speaking
  //   "recording"  — speech detected, recorder running, watching for silence
  const [vadState, setVadState] = useState("off");
  // VAD (conversational auto-listen) is always on. When the AI finishes
  // speaking, the mic opens automatically. As a fallback for edge cases where
  // VAD's analyser isn't picking up speech (some iOS Safari builds, over-
  // AirPods routing, or a stuck ambient audio session), a manual "Speak now"
  // button below stays live during VAD-listen and lets the candidate override
  // by cancelling VAD and starting an immediate recording.
  const _isMobileUA = typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const [vadEnabled] = useState(true);
  const vadEnabledRef = useRef(true);
  useEffect(() => { vadEnabledRef.current = vadEnabled; }, [vadEnabled]);
  const vadStreamRef = useRef(null);
  const vadCtxRef = useRef(null);
  const vadAnalyserRef = useRef(null);
  const vadRafRef = useRef(null);
  const vadSilenceStartRef = useRef(null);   // timestamp when we last saw silence
  const vadHasSpokenRef = useRef(false);     // set true once we've crossed the speech threshold
  const vadSpeechStartRef = useRef(null);    // timestamp when the candidate first crossed the speech threshold (used for adaptive silence hold)

  // Unlock browser audio autoplay by priming the <audio> element during the
  // user gesture (miccheck-proceed click). Chrome/Safari allow subsequent
  // programmatic .play() calls once ANY audio has played from a real gesture.
  // We AWAIT the play() promise so the unlock is guaranteed complete before
  // startSession() fires the async API call — Chrome revokes activation on
  // some codepaths if the priming play hasn't actually started.
  const primeAudio = async () => {
    const el = audioPlayerRef.current;
    if (!el) return;
    try { el.setAttribute("playsinline", ""); el.setAttribute("webkit-playsinline", ""); } catch (_) { /* noop */ }
    el.muted = false;
    el.volume = 1.0;
    // Prime the Web Audio graph — iOS requires an AudioContext to be resumed
    // during a real user gesture for the tab to hold a media-session slot.
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx && !window.__compassAudioCtx) {
        window.__compassAudioCtx = new Ctx();
      }
      if (window.__compassAudioCtx?.state === "suspended") {
        await window.__compassAudioCtx.resume();
      }
    } catch (_) { /* noop */ }
    // Play tiny silent MP3 and LET IT COMPLETE — pausing immediately can
    // invalidate the audio session that iOS just granted.
    el.src = "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQxAADB8AhSmxhIIEVCSiJrDCQBTcu3UrAIwUdkRgQbFAZC1CQEwTJ9mjRvBA4UOLD8nKVOWfh+UlK3z/177OXrfOdKl7pyn3Xf//WreyTRUoAWgBgkOAGbZHBgG1OF6zM82DWbZaUmMBptgQhGjsyYqc9ae9XFz84FfHrOHfrPRxpXfstOltvpr8j1/vf/7v3vXn/9vNZKQ7QAAADACwCoAAAAAA//sUxAADwAABpAAAACAAADSAAAAETEFNRTMuMTAwVVU=";
    try { await el.play(); } catch (_) { /* if this fails autoplay was going to fail anyway */ }
  };

  // On iPhone (Safari), even after primeAudio the very first real utterance
  // occasionally silently routes to the "ambient" channel when the ringer
  // switch is silent — the code sees `.play()` resolve, but nothing comes
  // out. Detect the mobile case and force an explicit tap on the FIRST turn.
  const isIosOrAndroid = /iPhone|iPad|iPod|Android/i.test(
    typeof navigator !== "undefined" ? navigator.userAgent : ""
  );

  useEffect(() => {
    api.get("/participant/salescert/roleplay/next")
      .then((r) => {
        setScenario(r.data);
        setRemaining(r.data.duration_seconds);
        setPhase("briefing");
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail || String(e);
        if (e?.response?.status === 423) {
          const hid = extractHoldId(detail);
          if (hid) { setHoldId(hid); setPhase("hard_stop"); return; }
        }
        // Exhausted / forfeited attempts → silently bounce to the hub;
        // the section card there already communicates the state.
        if (isAttemptExhaustedError(e)) {
          nav("/participant/salescert", { replace: true });
          return;
        }
        setErr(detail);
        setPhase("error");
      });
    return () => timerRef.current && clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (phase !== "active") return;
    // iter48 — hold the countdown until the opening audio has finished
    // (or we've given up on TTS). Prevents a 10-12 s silent burn-off at
    // the top of every role-play.
    if (!openingAudioFinished) return;
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(timerRef.current);
          if (!submittedRef.current) submitSession(true);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, openingAudioFinished]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [session]);

  // Tear VAD down when the component unmounts (candidate navigates away
  // mid-turn, phase changes to scored, etc.) so the mic actually releases.
  useEffect(() => () => { teardownVad(); }, []);

  // Single-attempt integrity guard: warn the candidate if they try to close
  // the tab, refresh, or navigate away while the role-play is `active`. The
  // browser shows its own native "Leave site?" prompt — we can only trigger
  // it, we can't customise the copy on modern browsers. If they proceed
  // anyway, the server auto-forfeits the attempt (0 marks) on next /start.
  useEffect(() => {
    if (phase !== "active") return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [phase]);

  // Play the AI's audio AFTER the active phase mounts the <audio> element.
  // Without this, calling playAudio() inside startSession() runs before the
  // <audio> tag exists (audioPlayerRef.current is null) — so the opening line
  // was silent while every subsequent turn (played from within the already-
  // mounted active view) worked fine.
  const [pendingAudio, setPendingAudio] = useState(null);
  // Opening-audio queue — once the active-view mounts and audioPlayerRef is
  // populated, play whatever /start returned. This runs in a useEffect so
  // the ref is guaranteed to be attached when playAudio touches it.
  // iter48 — the opening also seeds `aiReveal` so the opening line text is
  // written out WORD-BY-WORD in sync with the opening audio (not shown in
  // full the moment the room mounts). iter58 continues this: opening is
  // tagged as turn 0 for the dedupe guard.
  useEffect(() => {
    if (phase === "active" && pendingAudio) {
      // Seed the reveal state BEFORE anything else so even the first render
      // where the opening audio is being queued already slices the message
      // (prevents the "flash of full text, then reveal starts" race).
      const openingMsg = (session?.messages || []).find(
        (m) => m.role === "assistant" && (m.turn === 0 || m.turn === undefined)
      );
      if (openingMsg) {
        setAiReveal({ turn: openingMsg.turn ?? 0, text: openingMsg.content, chars: 0 });
      }
      playAudio(pendingAudio, openingMsg?.turn ?? 0);
      setPendingAudio(null);
    }
  }, [phase, pendingAudio]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Background reconciler — polls /session-state every 3 s while the room
  // is active. Belt AND braces for iOS Safari, which is prone to dropping
  // deferred setState callbacks after long-running WebAudio + fetch chains
  // (specifically the first turn, where the browser is still holding the
  // opening-audio autoplay unlock gesture). Polling means any orphaned turn
  // will surface on the very next tick, not later than 3 s after the DB
  // saves it. Cheap: the endpoint returns a slim messages array (no audio).
  useEffect(() => {
    if (phase !== "active") return undefined;
    const iv = setInterval(() => { reconcilerRef.current(); }, 3000);
    // Also fire once immediately on mount so if we entered "active" with
    // any drift, it corrects on tick 0.
    reconcilerRef.current();
    return () => clearInterval(iv);
  }, [phase]);

  // Reconcile whenever the tab returns to the foreground — iOS Safari
  // suspends setInterval when the screen locks / user switches apps, so
  // a dropped turn that landed during suspension would otherwise stay
  // invisible until the next 3 s tick.
  useEffect(() => {
    if (phase !== "active") return undefined;
    const onVis = () => {
      if (document.visibilityState === "visible") reconcilerRef.current();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    window.addEventListener("pageshow", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
      window.removeEventListener("pageshow", onVis);
    };
  }, [phase]);

  // Briefing "Enter the room" → mic-check gate → startSession → active
  const goToMicCheck = () => setPhase("mic_check");

  const startSession = async () => {
    setPhase("loading");
    // iter48 — reset the opening-audio gate every start. Timer stays frozen
    // until onEnded fires OR the fallback below trips.
    setOpeningAudioFinished(false);
    // iter58 — reset the audio-dedupe stamp set so a fresh attempt isn't
    // blocked by turn numbers remembered from a previous session.
    playedTurnsRef.current = new Set();
    presentingTurnsRef.current = new Set();
    setAiReveal(null);
    // iter59 — reset the completed-reveal set so a fresh attempt's
    // opening message starts out hidden until its audio plays.
    setCompletedReveals(new Set());
    trackClick("salescert_roleplay_start", { scenario_id: scenario?.id, competency: scenario?.competency });
    try {
      const r = await api.post("/participant/salescert/roleplay/start", { scenario_id: scenario.id });
      setSession(r.data);
      // Queue the opening audio for playback AFTER the active view mounts,
      // so audioPlayerRef.current is guaranteed to be set. See useEffect above.
      // iter40 — TTS is now synthesised out-of-band on the server, so /start
      // returns `opening_audio_b64: null, audio_tts_status: "pending"`. Poll
      // /turn-audio/<session_id>/0 until the audio is ready (or failed), then
      // queue it like before. We pass session.id EXPLICITLY because React
      // hasn't flushed `setSession(r.data)` yet when this fires — reading
      // `session` from closure would give us the pre-start `null`. (iter41 fix.)
      if (r.data.opening_audio_b64) {
        setPendingAudio(r.data.opening_audio_b64);
      } else {
        // Fire-and-forget — resolves once TTS is ready or gives up after ~15s.
        fetchTurnAudio(0, r.data.id).then((audio) => {
          if (audio) {
            setPendingAudio(audio);
          } else {
            // iter48 — TTS gave up. Release the timer gate so the candidate
            // isn't stuck with a frozen 5:00 countdown + "Preparing opening"
            // banner forever when text-only resume is their only option.
            setOpeningAudioFinished(true);
            // iter59 — the opening message is already in session.messages
            // from /start, but the render gate hides un-revealed assistant
            // bubbles. Without audio there's nothing to pace a reveal
            // against, so stamp turn 0 as "completed" so the full opening
            // line appears for the candidate.
            markRevealComplete(0);
          }
        });
      }
      setPhase("active");
      // iter48 safety net — if the audio element never fires onEnded within
      // 45 s of entering the active phase (audio stuck buffering on iOS,
      // user never tapped the autoplay-unlock overlay, etc.) release the
      // timer gate anyway so the role-play isn't permanently frozen.
      // iter59 — also unhide the opening message so the candidate isn't
      // staring at an empty transcript. Reveal already got a chance to
      // start in the 45 s window; if it didn't, show the full text.
      setTimeout(() => {
        setOpeningAudioFinished((v) => v || true);
        markRevealComplete(0);
      }, 45000);
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
      setPhase("error");
    }
  };

  const [audioBlocked, setAudioBlocked] = useState(false);   // true when browser blocked autoplay → show "Tap to hear customer" overlay

  // Convert a raw base64 audio payload to a Blob URL. Chrome iOS has known
  // bugs decoding long `data:audio/...;base64,...` URIs on `<audio>` elements
  // — it silently fails to buffer the media. Blob URLs work around this.
  const _base64ToBlobUrl = (b64, mime = "audio/wav") => {
    try {
      const raw = atob(b64);
      const arr = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
      return URL.createObjectURL(new Blob([arr], { type: mime }));
    } catch (_) {
      return null;
    }
  };

  const playAudio = (b64, turn = null) => {
    if (!b64) return false;
    // iter58 — dedupe guard. If the same turn's audio has already been
    // handed to the <audio> element (direct from `presentAiTurn` OR via
    // the reconciler's recovery path), refuse to re-source it so the
    // first-two-words-repeat regression can't come back. Opening (turn 0)
    // and ad-hoc replays (turn=null) always play — those paths have
    // their own gating.
    if (turn !== null && turn !== undefined && playedTurnsRef.current.has(turn)) {
      return false;
    }
    try {
      let src = null;
      if (b64.startsWith("data:")) {
        src = b64;
      } else {
        // Detect WAV (RIFF) vs MP3 by header byte, then hand back a Blob URL
        // for maximum mobile browser compatibility.
        const head = b64.slice(0, 8);
        const mime = head.startsWith("UklG") ? "audio/wav" : "audio/mpeg";
        src = _base64ToBlobUrl(b64, mime);
      }
      if (!src) return false;
      if (audioPlayerRef.current) {
        // Revoke previous Blob URL to avoid the tab holding megabytes forever
        // during a long role-play conversation.
        const prev = audioPlayerRef.current.src;
        if (prev && prev.startsWith("blob:")) {
          try { URL.revokeObjectURL(prev); } catch (_) { /* noop */ }
        }
        audioPlayerRef.current.src = src;
        audioPlayerRef.current.load();  // force iOS to buffer the new source
        if (turn !== null && turn !== undefined) {
          playedTurnsRef.current.add(turn);
        }
        // On mobile the FIRST audio must be unlocked by a user gesture (the
        // "Tap here to hear the customer" button). Once we've done that once,
        // the <audio> element is marked "unlocked" and subsequent .play()
        // calls work without another tap — so we only surface the tap-to-hear
        // overlay on turn 1 (or after a genuine autoplay rejection).
        if (isIosOrAndroid && !audioUnlockedRef.current) {
          setAudioPlaying(false);
          setAudioBlocked(true);
          return true;
        }
        audioPlayerRef.current.play()
          .then(() => { setAudioPlaying(true); setAudioBlocked(false); audioUnlockedRef.current = true; })
          .catch(() => { setAudioPlaying(false); setAudioBlocked(true); });   // ← if the browser DID reject autoplay, surface the tap fallback
      }
      return true;
    } catch (e) { /* ignore */ }
    return false;
  };

  const forcePlayBlockedAudio = (evt) => {
    // CRITICAL: on iOS Safari / Chrome iOS the "user gesture" credit
    // expires the moment you `await` anything after the tap. So the first
    // thing we do inside this handler — synchronously, before any async
    // work — is call `.play()`. Everything else (AudioContext resume, error
    // reporting) happens AFTER the play promise fires.
    const el = audioPlayerRef.current;
    if (!el || !el.src) return;
    try { el.muted = false; el.volume = 1.0; } catch (_) { /* noop */ }
    el.currentTime = 0;
    const p = el.play();  // ← synchronous, inside the tap gesture
    if (p && typeof p.then === "function") {
      p.then(() => {
        setAudioPlaying(true);
        setAudioBlocked(false);
        setErr("");
        audioUnlockedRef.current = true;   // ← subsequent AI turns can autoplay on this element
        // Now (async) resume any suspended AudioContext so VAD works later.
        // This is a best-effort clean-up; playback has already started so
        // failure here does not affect the current turn.
        try {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          if (Ctx && !window.__compassAudioCtx) window.__compassAudioCtx = new Ctx();
          if (window.__compassAudioCtx?.state === "suspended") {
            window.__compassAudioCtx.resume().catch(() => {});
          }
        } catch (_) { /* noop */ }
      }).catch((e) => {
        // Genuine failure — surface actionable copy for iPhone silent-switch.
        setErr(
          "Couldn't play audio. If you're on iPhone, flip the physical ringer / silent switch to RING (side of the phone), turn the volume UP, then tap again. If it still fails, tap the Replay button below."
        );
        // eslint-disable-next-line no-console
        console.warn("[roleplay] forcePlayBlockedAudio failed:", e?.name, e?.message);
      });
    }
  };

  const startRecording = async (existingStream = null) => {
    if (thinking || recordingRef.current) return;
    try {
      // If VAD already opened a mic stream for its analyser, reuse it so we
      // don't spin up a second concurrent getUserMedia (wasteful + can trigger
      // AEC/ducking oddities on some browsers). Otherwise open a fresh one.
      const stream = existingStream || await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const preferred = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", ""];
      const mime = preferred.find((t) => !t || (window.MediaRecorder && MediaRecorder.isTypeSupported(t))) || "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        // Package whatever chunks we collected up to this point. If VAD passed
        // us its stream, VAD's own cleanup runs AFTER we assemble the blob so
        // the browser doesn't drop the final chunk when the tracks are killed.
        const blob = new Blob(audioChunksRef.current, { type: mime || "audio/webm" });
        if (existingStream) {
          teardownVad();
          setVadState("off");
        } else {
          stream.getTracks().forEach((t) => t.stop());
        }
        // Guard against zero-byte blobs (very fast turns / mic hiccup): fall
        // back to the text input rather than send empty audio to the backend.
        if (!blob || blob.size < 500) {
          setErr("We didn't catch that — please try speaking again in a clearer voice.");
          // Re-arm VAD so the candidate can just speak again immediately (no
          // AI reply is coming, so the audio-onEnded → startVad chain never
          // fires on its own).
          if (vadEnabled && phase === "active" && turnsUsed < scenario.max_turns) {
            setTimeout(() => { startVad(); }, 350);
          }
          return;
        }
        await sendAudioTurn(blob);
      };
      // iter43 — wrap mr.start() so NotSupportedError / InvalidStateError
      // (e.g. a stale slice recorder from useProctor still holding the mic
      // on cross-section nav) never surfaces as a React dev-overlay.
      try {
        mr.start(500);   // emit a chunk every 0.5s so short turns still have data
      } catch (e) {
        if (!existingStream) stream.getTracks().forEach((t) => t.stop());
        setErr("Microphone is temporarily busy — please try again in a moment.");
        return;
      }
      recordingRef.current = true;
      setRecording(true);
    } catch (e) {
      setErr("Microphone permission required. Enable your mic in the browser and refresh — the Role Play is voice-only.");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && recordingRef.current) {
      recordingRef.current = false;
      setRecording(false);
      try { mediaRef.current.stop(); } catch (_) { /* already stopped */ }
    }
  };

  // ---------- Voice-Activity Detection (conversational auto-mode) ----------
  // After every AI turn's audio finishes, we auto-open the mic and listen.
  // When the candidate starts speaking (RMS > SPEECH_THRESHOLD), we start
  // MediaRecorder. When RMS stays below SILENCE_THRESHOLD for
  // SILENCE_HOLD_MS, we auto-stop and send. This makes the whole exchange
  // feel like a real 5-min phone call rather than a walkie-talkie.
  const SPEECH_THRESHOLD = 0.055;
  const SILENCE_THRESHOLD = 0.028;   // slightly less sensitive so soft speech isn't misread as silence
  // Adaptive silence-hold — natural speech has 0.5-1.2s pauses between clauses,
  // so a fixed short window cuts people off mid-thought. We use a longer hold
  // for short turns (candidate is still building their answer) and shrink it
  // for long turns (they're wrapping up).
  const SILENCE_HOLD_SHORT_MS = 1600;   // <6s of speech so far → be patient (iter63: was 2200)
  const SILENCE_HOLD_MID_MS   = 1400;   // 6-15s (iter63: was 1900)
  const SILENCE_HOLD_LONG_MS  = 1200;   // >15s → they're clearly done (iter63: was 1600)
  const MAX_TURN_MS = 45000;            // hard cap per turn so a stuck mic doesn't hang

  const teardownVad = () => {
    if (vadRafRef.current) cancelAnimationFrame(vadRafRef.current);
    vadRafRef.current = null;
    const ctx = vadCtxRef.current;
    if (ctx && ctx.state !== "closed") { try { ctx.close(); } catch (e) { /* noop */ } }
    vadCtxRef.current = null;
    vadAnalyserRef.current = null;
    const stream = vadStreamRef.current;
    if (stream) { try { stream.getTracks().forEach((t) => t.stop()); } catch (e) { /* noop */ } }
    vadStreamRef.current = null;
    vadHasSpokenRef.current = false;
    vadSilenceStartRef.current = null;
    vadSpeechStartRef.current = null;
  };

  const startVad = async () => {
    if (!vadEnabled || vadState !== "off" || thinking || recordingRef.current) return;
    if (turnsUsed >= scenario.max_turns) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      vadStreamRef.current = stream;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      vadCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      vadAnalyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      setVadState("listening");

      const turnStart = Date.now();
      const tick = () => {
        // Race guard — if the user flipped the toggle mid-loop, bail out cleanly.
        if (!vadEnabledRef.current) { teardownVad(); setVadState("off"); return; }
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / buf.length);
        const now = Date.now();

        // Phase A — listening for the first speech burst
        if (!vadHasSpokenRef.current) {
          if (rms > SPEECH_THRESHOLD) {
            vadHasSpokenRef.current = true;
            vadSpeechStartRef.current = now;
            vadSilenceStartRef.current = null;
            setVadState("recording");
            // Share the mic stream — pass VAD's stream so MediaRecorder doesn't
            // spin up a second getUserMedia call.
            startRecording(vadStreamRef.current);
          }
        } else {
          // Phase B — watching for a sustained silence to auto-stop.
          // Adaptive: longer hold while the candidate is still ramping up so
          // natural clause-pauses don't cut them off mid-thought.
          if (rms < SILENCE_THRESHOLD) {
            if (vadSilenceStartRef.current == null) vadSilenceStartRef.current = now;
            const speechDurationMs = now - (vadSpeechStartRef.current || now);
            const holdMs = speechDurationMs > 15000
              ? SILENCE_HOLD_LONG_MS
              : speechDurationMs > 6000
                ? SILENCE_HOLD_MID_MS
                : SILENCE_HOLD_SHORT_MS;
            if (now - vadSilenceStartRef.current >= holdMs) {
              // stopRecording() triggers MediaRecorder.onstop which now owns
              // teardownVad() so the final audio chunk isn't lost to a
              // prematurely-killed stream.
              stopRecording();
              return;
            }
          } else {
            vadSilenceStartRef.current = null;   // any voice above the floor resets the silence timer
          }
        }
        // Hard cap on turn length
        if (now - turnStart > MAX_TURN_MS && vadHasSpokenRef.current) {
          stopRecording();
          return;
        }
        vadRafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      setVadState("off");
      setErr("Microphone permission required. The Role Play is voice-only — enable your mic in the browser and try again.");
    }
  };

  const cancelVad = () => {
    teardownVad();
    setVadState("off");
  };

  const sendAudioTurn = async (blob) => {
    if (thinking) return;
    setThinking(true);
    setErr("");
    // 45 s ceiling for the whole (STT + Claude) turn. Beyond this we assume
    // the request is stalled — cancel it and fall back to /latest-turn.
    const abort = createAbortSignal(45000);
    try {
      const fd = new FormData();
      fd.append("session_id", session.id);
      fd.append("audio", blob, "turn.webm");
      const r = await api.post("/participant/salescert/roleplay/turn-audio", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        signal: abort.signal,
      });
      // Append the candidate line right away — that's the candidate's own
      // words, not blocked by TTS. The AI reply is deferred to
      // presentAiTurn() which waits for audio to be ready (or 3 s max)
      // before showing text + playing audio together.
      setSession((s) => ({
        ...s,
        messages: [
          ...s.messages,
          { role: "user", content: r.data.candidate_transcript, turn: r.data.turn },
        ],
      }));
      presentAiTurn(r.data.turn, r.data.ai_message, r.data.should_end);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 423) {
        // Ambient-voice / face-mismatch hard-stop opened between turns.
        // Pause the session immediately — identical behaviour to Sales
        // Pitch — until an admin resolves the hold from Security Holds.
        const hid = extractHoldId(detail);
        if (hid) { setHoldId(hid); setPhase("hard_stop"); return; }
      }
      if (status === 403) {
        // Voice-biometric mismatch: this turn was blocked before it reached
        // Claude. Show a clear, actionable message so the candidate knows to
        // speak in their own voice (same mic as at enrolment).
        setErr(
          detail ||
          "That turn was blocked because the voice didn't match your enrolled voiceprint. Please speak in your own voice, on the same mic you enrolled with, and try again."
        );
        setThinking(false);
      } else if (isTimeoutOrAbort(e)) {
        // Timed out or aborted — the server MAY have already saved the AI
        // reply before we gave up. Try recovering it from /latest-turn so
        // the candidate doesn't lose the customer's line. recoverLatestTurn
        // ultimately calls presentAiTurn which clears `thinking`.
        const recovered = await recoverLatestTurn();
        if (!recovered) {
          setErr(
            "The customer is taking too long to respond. Please tap the mic and try that turn again — your last message was saved."
          );
          setThinking(false);
        }
      } else {
        setErr(detail || String(e));
        setThinking(false);
      }
      // The MediaRecorder.onstop handler already tore down the mic stream and
      // set vadState back to "off". No AI reply came back → the audio-player
      // onEnded → startVad chain is never triggered. Re-arm VAD manually so
      // the candidate can just speak again, without having to click "Replay".
      // Reconcile FIRST so any turn the server actually saved before we
      // aborted lands on screen before the candidate speaks the next one —
      // otherwise the missed turn is silently orphaned in the DB.
      await reconcileSessionState();
      if (vadEnabled && phase === "active" && turnsUsed < scenario.max_turns) {
        setTimeout(() => { startVad(); }, 350);
      }
    } finally {
      abort.cancel();
      // Note: setThinking(false) is NOT called on the happy path — the
      // presentAiTurn helper clears it when the AI text is actually rendered
      // (either audio-ready or 3 s fallback), so the "Customer is thinking…"
      // banner stays up through the audio-fetch window.
    }
    // Belt-and-braces reconcile after the happy path too — cheap 1-round-trip
    // that catches any drift between optimistic FE state and DB truth
    // (e.g., if a StrictMode double-render swallowed one of the setSessions).
    reconcileSessionState();
  };

  const sendTurn = async () => {
    if (!draft.trim() || thinking) return;
    const msg = draft.trim();
    setDraft("");
    setThinking(true);
    setErr("");
    // Optimistically append the candidate line
    setSession((s) => ({
      ...s,
      messages: [...s.messages, { role: "user", content: msg, turn: (s.messages.filter(m => m.role === "user").length + 1) }],
    }));
    const abort = createAbortSignal(45000);
    try {
      const r = await api.post(
        "/participant/salescert/roleplay/turn",
        { session_id: session.id, message: msg },
        { signal: abort.signal },
      );
      presentAiTurn(r.data.turn, r.data.ai_message, r.data.should_end);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 423) {
        // Ambient-voice / face-mismatch hard-stop opened mid-session —
        // switch to hard-stop screen, blocking further /turn attempts
        // until an admin resolves the hold.
        const hid = extractHoldId(detail);
        if (hid) { setHoldId(hid); setPhase("hard_stop"); return; }
      }
      if (isTimeoutOrAbort(e)) {
        const recovered = await recoverLatestTurn();
        if (!recovered) {
          setErr(
            "The customer is taking too long to respond. Please try that turn again — your last message was saved."
          );
          setThinking(false);
        }
      } else {
        setErr(detail || String(e));
        setPhase("error");
        setThinking(false);
      }
    } finally {
      abort.cancel();
      // Reconcile FE ↔ DB after every /turn call — success or error — so any
      // turn that was persisted but never rendered locally lands on screen.
      reconcileSessionState();
    }
  };

  // --- helpers for the split text-then-audio flow -------------------------

  // True when the error is a client-side timeout or user abort. axios raises
  // ECONNABORTED for its own `timeout` and CanceledError when an
  // AbortController signal fires.
  const isTimeoutOrAbort = (e) =>
    e?.code === "ECONNABORTED" ||
    e?.name === "CanceledError" ||
    e?.name === "AbortError" ||
    /timeout|aborted/i.test(e?.message || "");

  // Fetch TTS audio for a given turn number and RETURN the base64 payload
  // (does not play it). Returns null on any failure. Called by
  // presentAiTurn which owns the audio vs text-timeout race.
  //
  // iter40 — the backend now synthesises TTS as a background task so this
  // endpoint can return `status: "pending"` while Sarvam is still working.
  // Light polling (up to ~15 s total, 500 ms gap) keeps the behavioural
  // contract — a resolved promise with the base64 or null — identical to
  // the pre-refactor one-shot GET. Reconciler/VAD are untouched.
  //
  // iter41 — accepts an optional `overrideSessionId` so callers that have
  // JUST received a new session id (e.g. `startSession` after /roleplay/start)
  // can poll before React has flushed `setSession(...)`. Without this
  // override, closure-captured `session` would still be `null`, the opening
  // audio would silently never play, and the whole audio→VAD→mic chain
  // would stay dormant for the first turn.
  const fetchTurnAudio = async (turn, overrideSessionId) => {
    const sid = overrideSessionId || session?.id;
    if (!sid || turn === undefined || turn === null) return null;
    const abort = createAbortSignal(40000);
    const maxAttempts = 30;    // 30 * 500ms = 15 s overall budget
    try {
      for (let i = 0; i < maxAttempts; i++) {
        let r;
        try {
          r = await api.get(
            `/participant/salescert/roleplay/turn-audio/${sid}/${turn}`,
            { signal: abort.signal },
          );
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("TTS fetch failed", e?.message || e);
          return null;
        }
        const audio = r.data?.ai_audio_b64 || null;
        if (audio) return audio;
        const status = r.data?.status;
        if (status === "failed") return null;
        if (status !== "pending") return null;  // unknown shape — bail
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      return null;  // never resolved within the budget
    } finally {
      abort.cancel();
    }
  };

  // Present an AI turn with synchronised text + audio.
  //
  // iter56 behaviour (improved sync):
  //   1. "Customer is thinking…" stays visible UNTIL the TTS audio has been
  //      fetched. The text is NO LONGER revealed early — out-of-sync text
  //      is more confusing than a slightly longer thinking banner.
  //   2. When the audio arrives, we append the AI message AND start audio
  //      playback simultaneously, and seed `aiReveal` so the message
  //      content is revealed character-by-character paced to the audio's
  //      currentTime / duration (see <audio onTimeUpdate> below).
  //   3. If TTS truly fails or takes longer than 15 s, we fall back to
  //      instant text so the session can continue rather than hanging on
  //      a frozen thinking banner. This is a SAFETY NET, not the happy
  //      path like iter40 → iter55.
  //   4. Clear `thinking` the moment the text becomes visible so the
  //      banner tracks the actual delay the candidate experienced.
  const presentAiTurn = (turn, text, should_end) => {
    if (!turn || !text) {
      setThinking(false);
      return;
    }
    // iter60 — Seed the reveal IMMEDIATELY, before any async work. This
    // is the critical atomicity guarantee the render gate depends on:
    // any render that includes this turn's assistant message (whether it
    // got there via the normal appendText path OR via a reconciler merge
    // firing in parallel) will see `aiReveal.turn === turn` and slice to
    // `aiReveal.chars` chars. Starting at chars=0 means those pre-audio
    // renders show an empty bubble + caret (which the "Customer is
    // thinking…" banner above already explains) instead of a full-text
    // flash. The reveal only animates once audio starts (onTimeUpdate).
    setAiReveal({ turn, text, chars: 0 });
    presentingTurnsRef.current.add(turn);

    let settled = false;
    let audioStarted = false;

    const appendText = (b64) => {
      if (settled) return;
      settled = true;
      // iter60 — reveal was already seeded at the top of presentAiTurn.
      // On the no-audio fallback path we clear it and stamp completed so
      // the full text surfaces for the candidate; otherwise we keep the
      // seeded reveal and let onTimeUpdate advance chars with playback.
      if (!b64) {
        setAiReveal(null);
        markRevealComplete(turn);
      }
      setSession((s) => {
        const already = (s.messages || []).some(
          (m) => m.role === "assistant" && m.turn === turn
        );
        if (already) return s;
        return {
          ...s,
          messages: [...s.messages, { role: "assistant", content: text, turn }],
        };
      });
      if (b64) {
        playAudio(b64, turn);
        audioStarted = true;
      }
      setThinking(false);
      if (should_end) submitSession(false);
    };

    // iter56 — primary path: wait for audio, then reveal text + play
    // together. Fallback timer bumped from 3 s → 15 s so a slow Sarvam
    // doesn't force out-of-sync text; the backend's own TTS budget is
    // ~12 s so this safety net only fires on a true outage.
    const safetyTimer = setTimeout(() => {
      if (!audioStarted) appendText(null);
    }, 15000);

    fetchTurnAudio(turn).then((b64) => {
      clearTimeout(safetyTimer);
      if (settled) {
        // Safety net already fired (TTS came back AFTER 15 s). Play the
        // audio now out-of-sync — text is already up, no reveal.
        // iter58 — dedupe guarded via `turn` so the reconciler path can't
        // double-trigger playback for the same message.
        if (b64) playAudio(b64, turn);
        return;
      }
      appendText(b64);
    });
  };

  // After a turn request times out / errors, ask the server for the latest
  // persisted AI reply. If it's newer than the last one we've rendered,
  // route it through presentAiTurn so the recovered turn also gets the
  // audio-then-text synchronisation.
  const recoverLatestTurn = async () => {
    if (!session?.id) return false;
    try {
      const r = await api.get(`/participant/salescert/roleplay/latest-turn/${session.id}`);
      const data = r.data || {};
      if (!data.ai_message || !data.turn) return false;
      const currentMaxAssistantTurn = (session?.messages || [])
        .filter((m) => m.role === "assistant")
        .reduce((acc, m) => Math.max(acc, m.turn || 0), 0);
      if (data.turn <= currentMaxAssistantTurn) return false; // nothing new
      // If our optimistic user message never got confirmed and the server
      // has a candidate_transcript for the same turn, patch it in too.
      const currentMaxUserTurn = (session?.messages || [])
        .filter((m) => m.role === "user")
        .reduce((acc, m) => Math.max(acc, m.turn || 0), 0);
      if (data.candidate_transcript && data.turn > currentMaxUserTurn) {
        setSession((s) => ({
          ...s,
          messages: [
            ...s.messages,
            { role: "user", content: data.candidate_transcript, turn: data.turn },
          ],
        }));
      }
      presentAiTurn(data.turn, data.ai_message, data.should_end);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Reconcile the local `session.messages` array with the DB's authoritative
  // copy. Called after every turn (success and error paths), on a background
  // interval while the room is active, and on tab-visibility restore so a
  // dropped turn can never stay dropped for more than a few seconds.
  //
  // Uses `sessionRef.current` (not the render closure) so callbacks fired
  // long after the initial render — mr.onstop chains on iOS Safari, resumed
  // setIntervals after screen-wake, visibilitychange handlers — always see
  // the latest session id and messages.
  //
  // Behaviour: any DB messages with `turn` > our current max for that role
  // are appended in order. If audio hasn't played for a freshly-recovered
  // assistant turn, kick off `fetchTurnAudio` so TTS still fires.
  const reconcileSessionState = async () => {
    const sid = sessionRef.current?.id;
    if (!sid) return;
    // One quick retry on transient failure — mobile networks flap and we
    // don't want a single lost reconcile to leave a turn stranded.
    let serverMsgs = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const r = await api.get(`/participant/salescert/roleplay/session-state/${sid}`, { timeout: 15000 });
        serverMsgs = r.data?.messages || [];
        break;
      } catch (_) {
        if (attempt === 0) await new Promise((res) => setTimeout(res, 700));
      }
    }
    if (!serverMsgs || !serverMsgs.length) return;
    let recoveredAiTurn = null;
    setSession((s) => {
      const current = s?.messages || [];
      // Build a set of (role, turn) tuples we already have on screen.
      const have = new Set(current.map((m) => `${m.role}:${m.turn}`));
      const missing = serverMsgs.filter((m) => !have.has(`${m.role}:${m.turn}`));
      if (!missing.length) return s;
      // Sort merged messages by turn, then by role (user before assistant
      // in the same turn) so the transcript reads chronologically.
      const merged = [...current, ...missing].sort((a, b) => {
        const t = (a.turn || 0) - (b.turn || 0);
        if (t !== 0) return t;
        return a.role === "user" ? -1 : 1;
      });
      // Remember the latest assistant turn we recovered so its TTS still fires.
      const latestAi = missing
        .filter((m) => m.role === "assistant")
        .reduce((acc, m) => (!acc || (m.turn || 0) > (acc.turn || 0) ? m : acc), null);
      if (latestAi) recoveredAiTurn = latestAi.turn;
      // iter59/iter60 — any assistant message surfaced by the reconciler
      // happened in a prior render / tab (either the user navigated away
      // mid-session or we lost the setSession call to a race). Stamp them
      // as reveal-completed so the render gate shows them in full;
      // otherwise the transcript would stay blank even after recovery
      // because the normal `presentAiTurn` reveal path never ran for
      // these messages.
      //
      // iter60 — EXCEPT when the normal reveal IS already in flight for
      // that turn (presentAiTurn seeded aiReveal before the API returned
      // and before this reconciler fetched /session). Force-completing
      // that turn here would make the bubble flash the full text, then
      // snap back to the slice when the audio ticks start. Reading
      // through `aiRevealRef` because the state captured in this
      // closure is stale.
      const inFlightTurn = aiRevealRef.current?.turn;
      missing
        .filter((m) => m.role === "assistant" && m.turn !== undefined)
        .forEach((m) => {
          if (inFlightTurn !== undefined && m.turn === inFlightTurn) return;
          markRevealComplete(m.turn);
        });
      return { ...s, messages: merged };
    });
    // Fire TTS for the recovered assistant reply (if any). iter58 — tag
    // with the recovered turn so if `presentAiTurn` is ALSO handling the
    // same turn in parallel we don't re-source the <audio> element mid
    // playback and audibly repeat the first words.
    if (recoveredAiTurn && !presentingTurnsRef.current.has(recoveredAiTurn)) {
      fetchTurnAudio(recoveredAiTurn).then((b64) => { if (b64) playAudio(b64, recoveredAiTurn); });
    }
  };
  // Keep reconcilerRef pointed at the latest closure so interval /
  // visibilitychange / audio-ended handlers always call the freshest one.
  reconcilerRef.current = reconcileSessionState;

  const submitSession = async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setPhase("scoring");
    trackClick("salescert_roleplay_submit", { auto, session_id: session?.id, turns_used: session?.messages?.filter((m) => m.role === "user").length || 0 });
    try {
      const fd = new FormData();
      fd.append("session_id", session.id);
      const r = await api.post("/participant/salescert/roleplay/submit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const status = await api.get("/participant/salescert/status").then((x) => x.data).catch(() => null);
      const nxt = nextSalescertSection(status, "roleplay");
      setResult({ ...r.data, auto_ended: auto, next: nxt });
      setPhase("scored");
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
      setPhase("error");
    }
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  // Single persistent audio element rendered at every phase's root — ref stays
  // valid across mic_check → active transitions (no momentary null window).
  // primeAudio() + playAudio() both target this exact node.
  const audioNode = (
    <audio
      ref={audioPlayerRef}
      className="hidden"
      data-testid="salescert-rp-audio-player"
      // eslint-disable-next-line react/no-unknown-property
      playsInline
      preload="auto"
      controls={false}
      onPlay={() => setAudioPlaying(true)}
      onTimeUpdate={(e) => {
        // iter56 — pace the AI text reveal to the audio playback. We only
        // update if (a) a reveal is active, (b) the audio has a known
        // finite duration (metadata loaded), and (c) the computed char
        // count has actually changed, to avoid pointless re-renders.
        const el = e.currentTarget;
        if (!el.duration || !isFinite(el.duration)) return;
        setAiReveal((rs) => {
          if (!rs) return rs;
          const pct = Math.max(0, Math.min(1, el.currentTime / el.duration));
          const target = Math.min(rs.text.length, Math.floor(pct * rs.text.length));
          if (target <= rs.chars) return rs;
          return { ...rs, chars: target };
        });
      }}
      onEnded={() => {
        setAudioPlaying(false);
        // iter56 — finalise the reveal (every character on screen) at the
        // exact moment the voice stops so the candidate reads the full
        // line as the audio tails off.
        // iter59 — stamp this turn as "reveal completed" BEFORE clearing
        // the reveal state so the next render sees it in the completed
        // set and keeps the bubble visible (as a static full-text one).
        setAiReveal((rs) => {
          if (rs) markRevealComplete(rs.turn);
          return null;
        });
        // iter48 — The first `onEnded` we see after entering the room is
        // the AI customer's OPENING line finishing. That's when the
        // candidate's actual turn begins → release the 5-minute timer
        // gate and clear the "Customer is speaking…" indicator.
        setOpeningAudioFinished(true);
        // Belt-and-braces: reconcile once the AI audio finishes. If the AI
        // reply text was dropped by an iOS Safari setState race, this
        // guarantees it lands on screen before the candidate starts speaking
        // their next reply.
        reconcilerRef.current();
        // Conversational mode — the moment the AI finishes speaking, open
        // the mic and start listening for the candidate's reply.
        if (vadEnabled && phase === "active" && !thinking && !recordingRef.current && turnsUsed < scenario.max_turns) {
          setTimeout(() => startVad(), 200);   // tiny debounce so the OS mic device releases first
        }
      }}
      onPause={() => setAudioPlaying(false)}
    />
  );

  if (phase === "loading") return <>{audioNode}<div className="p-10 text-sm text-[#878787]">Loading…</div></>;
  if (phase === "hard_stop") return (
    <SecurityHardStop
      holdId={holdId}
      onResolvedResume={() => window.location.reload()}
      onResolvedReset={() => window.location.reload()}
    />
  );

  if (phase === "error") return (
    <div className="max-w-2xl mx-auto p-8 fade-in" data-testid="salescert-rp-error">
      <AlertTriangle className="text-[#EE2737] mb-3" size={28} />
      <h2 className="font-heading text-2xl font-bold mb-2">Something went wrong</h2>
      <p className="text-sm text-[#525252] mb-6">{err}</p>
      <button onClick={() => nav("/participant/salescert")} className="btn btn-secondary">Back to certification</button>
    </div>
  );

  if (phase === "briefing") return (
    <div className="max-w-3xl mx-auto p-6 space-y-6 fade-in" data-testid="salescert-rp-briefing">
      <div>
        <div className="overline mb-2">AI CUSTOMER ROLE PLAY · 25 MARKS · 3 MIN</div>
        <h1 className="font-heading text-3xl font-bold">Meet your customer</h1>
      </div>
      <div className="card p-6">
        <div className="overline mb-2">SCENARIO</div>
        <p className="text-sm text-[#0A0A0A] leading-relaxed whitespace-pre-wrap" data-testid="salescert-rp-scenario">
          {scenario.scenario}
        </p>
        <div className="mt-4 pt-4 border-t border-[#E5E7EB] text-xs text-[#878787]">
          <span className="font-bold text-[#0A0A0A]">Their vantage:</span> {scenario.role_vantage || "Senior enterprise buyer"}
        </div>
      </div>
      <div className="card p-6 bg-[#F8F9FA]">
        <div className="overline mb-2">HOW THIS WORKS</div>
        <ul className="text-sm text-[#525252] space-y-2 leading-relaxed">
          <li>• The AI customer speaks first · you have <strong>5 minutes</strong> total</li>
          <li>• Up to <strong>{scenario.max_turns} exchanges</strong> · session ends at max turns or time-up</li>
          <li>• You will be scored on opening, questioning, value framing, and handling pushback</li>
          <li className="text-[#991B1B]">• <strong>Single attempt only</strong> — do not close, reload, or leave this tab once you start. Leaving mid-attempt forfeits the section (0 marks).</li>
        </ul>
      </div>
      {isIosOrAndroid && (
        <div className="card p-5 bg-[#FFF8E1] border-l-4 border-[#EE2737]" data-testid="salescert-rp-mobile-notice">
          <div className="flex items-start gap-3">
            <Volume2 size={22} className="text-[#EE2737] flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-[#0A0A0A] mb-1">
                Important — iPhone / iPad / Android users
              </div>
              <p className="text-xs text-[#525252] leading-relaxed">
                The first time the customer speaks, a small hint will appear next to the <strong>Replay</strong> button — <strong>tap Replay once</strong> to unlock audio. After that, every reply plays automatically for the rest of the role play. Please <strong>turn up your volume</strong> and, if using AirPods, make sure they are connected before starting.
              </p>
            </div>
          </div>
        </div>
      )}
      <button
        onClick={goToMicCheck}
        className="btn btn-primary text-sm"
        data-testid="salescert-rp-start"
      >
        <Sparkles size={14} /> Enter the room
      </button>
    </div>
  );

  // Mic-check gate — sits between briefing and active so candidates confirm
  // their mic works before the AI opens the timed 5-min conversation. We ALSO
  // use the miccheck-proceed click as a user gesture to unlock browser audio
  // autoplay for the rest of the session (see primeAudio).
  if (phase === "mic_check") return (
    <>
      {audioNode}
      <MicCheckGate
        title="Let's check your mic before the role play"
        subtitle="The customer will speak first and expect you to respond by voice. We'll record 5 seconds of you speaking a test phrase and read it back so you know your mic is working. This is not scored."
        testPhrase="Testing one two three — I'm ready for the role play."
        proceedLabel={enrolCheck.inFlight ? "Confirming enrolment…" : "Perfect — enter the room"}
        proceedDisabled={enrolCheck.inFlight}
        onSecurityHold={({ holdId: hid }) => { setHoldId(hid); setPhase("hard_stop"); }}
        onPass={async () => {
          setEnrolCheck({ inFlight: true, error: "" });
          try {
            const status = await api.get("/participant/salescert/status").then((r) => r.data);
            if (!status?.voice_enrolled) {
              setEnrolCheck({
                inFlight: false,
                error: "We couldn't confirm your voice enrolment. Please try the mic-check again.",
              });
              return;
            }
          } catch (e) {
            setEnrolCheck({
              inFlight: false,
              error: e?.response?.data?.detail || "Enrolment check failed. Please try the mic-check again.",
            });
            return;
          }
          setEnrolCheck({ inFlight: false, error: "" });
          await primeAudio();
          startSession();
        }}
      />
      {enrolCheck.error && (
        <div
          className="max-w-3xl mx-auto mt-4 px-4 py-3 bg-[#FEE2E2] border-l-4 border-[#EE2737] text-[13px] text-[#991B1B] flex items-start gap-2"
          data-testid="rp-enrol-error"
        >
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">{enrolCheck.error}</div>
            <div className="text-xs mt-1 text-[#7F1D1D]">Retry the 5-second mic-check above to re-enrol your voice.</div>
          </div>
        </div>
      )}
    </>
  );

  if (phase === "scoring") return (
    <div className="max-w-2xl mx-auto p-10 flex items-center gap-3" data-testid="salescert-rp-scoring">
      <Loader2 className="animate-spin text-[#EE2737]" size={20} />
      <span className="text-sm text-[#525252]">Scoring your role play (~10–15s)…</span>
    </div>
  );

  if (phase === "scored") {
    const released = result.results_released !== false;
    if (!released) return (
      <div className="max-w-3xl mx-auto p-8 space-y-6 fade-in" data-testid="salescert-rp-scored">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="text-[#059669]" size={28} />
          <h2 className="font-heading text-3xl font-bold">Role play recorded</h2>
        </div>
        {result.auto_ended && (
          <div className="bg-[#FEF3C7] border border-[#F59E0B] text-[#92400E] text-xs px-4 py-2 uppercase tracking-widest font-bold">
            Session ended at time-up
          </div>
        )}
        <div className="card p-6" data-testid="salescert-rp-recorded-embargo">
          <div className="overline mb-2">RESPONSE RECORDED</div>
          <p className="text-sm text-[#0A0A0A] leading-relaxed">
            {result.message || "Your Role Play has been recorded. VI L&D will share your consolidated report and certificate once the assessment window closes."}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {result.can_retake && (
            <button
              onClick={() => window.location.reload()}
              className="btn btn-secondary text-sm justify-center"
              data-testid="salescert-rp-retake"
            >
              <RefreshCw size={14} /> Take Second Attempt
            </button>
          )}
          <button
            onClick={() => nav("/participant/salescert")}
            className="btn btn-primary text-sm justify-center"
            data-testid="salescert-rp-proceed"
          >
            Proceed to submit <ChevronRight size={14} />
          </button>
        </div>
      </div>
    );
    return (
    <div className="max-w-3xl mx-auto p-8 space-y-6 fade-in" data-testid="salescert-rp-scored">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="text-[#059669]" size={28} />
        <h2 className="font-heading text-3xl font-bold">Role play submitted</h2>
      </div>
      {result.auto_ended && (
        <div className="bg-[#FEF3C7] border border-[#F59E0B] text-[#92400E] text-xs px-4 py-2 uppercase tracking-widest font-bold">
          Session ended at time-up
        </div>
      )}
      <div className="card p-6">
        <div className="overline mb-2">SCORE</div>
        <div className="flex items-baseline gap-3">
          <div className="text-5xl font-black text-[#EE2737]" data-testid="salescert-rp-marks">{result.marks_awarded}</div>
          <div className="text-sm text-[#525252]">/ {result.marks_max} marks</div>
        </div>
        <div className="text-xs text-[#878787] mt-1">Rubric score: {result.score_1_to_5} / 5</div>
      </div>
      <div className="card p-6">
        <div className="overline mb-2">COACH&apos;S FEEDBACK</div>
        <p className="text-sm text-[#0A0A0A] leading-relaxed" data-testid="salescert-rp-feedback">{result.feedback}</p>
      </div>
      <details className="card p-6">
        <summary className="cursor-pointer text-xs uppercase tracking-widest text-[#525252] font-bold">View transcript</summary>
        <p className="text-sm text-[#525252] mt-3 leading-relaxed whitespace-pre-wrap" data-testid="salescert-rp-transcript">
          {result.transcript}
        </p>
      </details>
      <div className="grid sm:grid-cols-2 gap-3">
        {result.can_retake && (
          <button
            onClick={() => window.location.reload()}
            className="btn btn-secondary text-sm justify-center"
            data-testid="salescert-rp-retake"
          >
            <RefreshCw size={14} /> Take Second Attempt
          </button>
        )}
        <button
          onClick={() => nav("/participant/salescert")}
          className="btn btn-primary text-sm justify-center"
          data-testid="salescert-rp-proceed"
        >
          Proceed to submit <ChevronRight size={14} />
        </button>
      </div>
    </div>
    );
  }

  // phase === "active"
  const turnsUsed = session?.messages?.filter((m) => m.role === "user").length || 0;
  const lowTime = remaining <= 30;

  return (
    // NOTE: audioNode MUST render as a sibling of the active <div>, not
    // inside it, so the audio element's DOM parent stays identical to the
    // mic_check phase (both Fragments). Otherwise React re-mounts the
    // <audio> across the mic_check → active transition, wiping the browser's
    // "unlocked-by-user-gesture" state and making iOS reject subsequent
    // .play() calls silently — the exact bug that caused "AI customer
    // doesn't speak on mobile".
    <>
      {audioNode}
      <div className="max-w-3xl mx-auto p-4 space-y-4 fade-in relative" data-testid="salescert-rp-active">
      <SalesCertProctorHud />
      <IntegrityBanner />
      <IntegrityWatermark testid="rp-watermark" />
      {/* Visible "customer speaking" banner — pulses while audio plays */}
      {audioPlaying && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[#0A0A0A] text-white border-l-4 border-[#059669] animate-pulse" data-testid="salescert-rp-speaking">
          <Volume2 size={18} className="text-[#059669]" />
          <span className="text-sm font-bold">Customer is speaking…</span>
        </div>
      )}
      {/* Autoplay-blocked fallback.
          Desktop: rare — browsers usually let us prime audio via primeAudio.
          Keep the large red block so nobody misses it on the ~1% case.
          Mobile (iOS Safari / Android Chrome): the first turn ALWAYS lands
          here because mobile browsers refuse autoplay without a user gesture.
          Per Feb 2026 UX update, we no longer show a bottom banner on mobile.
          Instead, a small hint appears next to the Replay button below (see
          the Replay button block) and tapping Replay unlocks audio via
          forcePlayBlockedAudio, so turns 2+ autoplay. */}
      {audioBlocked && !audioPlaying && !isIosOrAndroid && (
        <button
          onClick={forcePlayBlockedAudio}
          className="w-full flex flex-col items-center justify-center gap-2 px-4 py-5 bg-[#EE2737] active:bg-[#B91C1C] hover:bg-[#D81929] text-white font-bold uppercase tracking-widest animate-pulse border-2 border-[#0A0A0A] shadow-lg"
          data-testid="salescert-rp-tap-to-hear"
        >
          <div className="flex items-center gap-3 text-base sm:text-lg">
            <Volume2 size={22} /> Tap here to hear the customer
          </div>
          <div className="text-[10px] font-medium normal-case tracking-normal text-white/85">
            Your browser blocked autoplay — tap once to unlock audio
          </div>
        </button>
      )}
      {/* iter48 — Opening-audio status banner. Shown while the AI customer's
          opening line is still being fetched (TTS pending), waiting on a
          mobile autoplay-unlock tap, or playing. Keeps the candidate from
          speaking into silence while the audio warms up, and matches the
          frozen 5-minute timer below. */}
      {!openingAudioFinished && (
        <div
          className="flex items-center gap-3 px-4 py-3 bg-[#F5F3EE] text-[#3C3A36] border-l-4 border-[#0A0A0A]"
          data-testid="salescert-rp-opening-banner"
        >
          {audioBlocked && isIosOrAndroid ? (
            <>
              <Volume2 size={18} className="text-[#EE2737] animate-pulse" />
              <span className="text-sm font-bold">Tap the pulsing Replay button below to hear the customer.</span>
            </>
          ) : audioPlaying ? (
            <>
              <Volume2 size={18} className="text-[#0A0A0A] animate-pulse" />
              <span className="text-sm font-bold">Customer is speaking… your turn starts when they finish.</span>
            </>
          ) : (
            <>
              <Loader2 size={18} className="text-[#0A0A0A] animate-spin" />
              <span className="text-sm font-bold">Preparing the customer&apos;s opening — please wait a few seconds.</span>
            </>
          )}
          <span className="ml-auto text-[10px] uppercase tracking-widest text-[#6B6860]">timer paused</span>
        </div>
      )}
      {/* Conversational voice-mode indicator — appears when we're listening or auto-recording */}
      {vadState === "listening" && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[#F5F3EE] text-[#3C3A36] border-l-4 border-[#EE2737]" data-testid="salescert-rp-listening">
          <Mic size={18} className="text-[#EE2737] animate-pulse" />
          <span className="text-sm font-bold">Your turn — start speaking whenever you&apos;re ready.</span>
          <span className="ml-auto text-[10px] uppercase tracking-widest text-[#6B6860]">or tap the mic below</span>
        </div>
      )}
      {vadState === "recording" && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[#0A0A0A] text-white border-l-4 border-[#EE2737] animate-pulse" data-testid="salescert-rp-vad-recording">
          <Mic size={18} className="text-[#EE2737]" />
          <span className="text-sm font-bold">Recording · auto-sends when you pause · or tap the green button ↓</span>
        </div>
      )}
      {thinking && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[#0A0A0A] text-white border-l-4 border-[#EE2737]" data-testid="salescert-rp-thinking-banner">
          <Loader2 size={18} className="text-[#EE2737] animate-spin" />
          <span className="text-sm font-bold">Sending your response · the customer is preparing their reply…</span>
        </div>
      )}
      <div
        className="sticky top-0 z-30 -mx-4 px-4 py-3 bg-white/95 backdrop-blur-sm border-b border-[#E5E7EB] flex items-center justify-between"
        data-testid="salescert-rp-sticky-header"
      >
        <div>
          <div className="overline">LIVE ROLE PLAY · {scenario.competency?.replace(/_/g, " ")}</div>
          <div className="text-xs text-[#525252] mt-1">
            Turn <span className="font-bold text-[#0A0A0A]">{turnsUsed}</span> of {scenario.max_turns}
          </div>
        </div>
        <div className={`flex items-center gap-2 px-4 py-2 font-heading font-black text-xl ${
          !openingAudioFinished
            ? "bg-[#E5E7EB] text-[#525252] opacity-70"
            : lowTime ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#0A0A0A] text-white"
        }`} data-testid="salescert-rp-timer">
          <Clock size={18} /> {mmss(remaining)}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="card p-4 h-[440px] overflow-y-auto space-y-4"
        data-testid="salescert-rp-transcript-live"
      >
        {session?.messages?.map((m, i) => {
          // iter59 — assistant bubbles are strictly gated:
          //   • If the message is CURRENTLY being revealed → show the
          //     sliced portion + typing caret.
          //   • Else if the reveal has already COMPLETED for this turn →
          //     show the full text (no caret).
          //   • Otherwise don't render the bubble AT ALL — the text must
          //     never leak before playback starts, which is what caused
          //     the "flash of full text" + "opening visible while timer
          //     is paused" regressions in iter56-58.
          // User messages are rendered unconditionally.
          if (m.role === "assistant") {
            const isRevealing = aiReveal && aiReveal.turn === m.turn && aiReveal.chars < m.content.length;
            const isCompleted = completedReveals.has(m.turn);
            if (!isRevealing && !isCompleted) return null;
            // iter61 — a seeded-but-not-yet-ticking reveal (chars === 0)
            // still rendered an empty bubble + caret alongside the
            // "Customer is thinking…" banner. Suppress the bubble until
            // audio playback has actually produced at least one character
            // of text (or the reveal has already completed). The banner
            // alone is enough while waiting for voice.
            if (isRevealing && !isCompleted && aiReveal.chars < 1) return null;
            const shown = isRevealing ? m.content.slice(0, aiReveal.chars) : m.content;
            return (
              <div key={i} className="flex gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-[#0A0A0A] text-white">
                  <Sparkles size={14} />
                </div>
                <div
                  className="max-w-[75%] p-3 text-sm leading-relaxed bg-[#0A0A0A]/5 border border-[#E5E7EB]"
                  data-testid={`salescert-rp-ai-message-turn-${m.turn}`}
                >
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#878787] mb-1">Customer</div>
                  {shown}
                  {isRevealing && (
                    <span className="inline-block w-[2px] h-4 bg-[#0A0A0A] ml-0.5 align-middle animate-pulse" aria-hidden />
                  )}
                </div>
              </div>
            );
          }
          return (
            <div key={i} className="flex gap-3 flex-row-reverse">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-[#EE2737] text-white">
                <User size={14} />
              </div>
              <div className="max-w-[75%] p-3 text-sm leading-relaxed bg-[#F5F5F5] text-[#0A0A0A]">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#878787] mb-1">You</div>
                {m.content}
              </div>
            </div>
          );
        })}
        {thinking && (
          <div className="flex gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#0A0A0A] text-white flex-shrink-0">
              <Sparkles size={14} />
            </div>
            <div className="bg-[#0A0A0A]/5 border border-[#E5E7EB] p-3 flex items-center gap-2 text-sm text-[#525252]">
              <Loader2 size={14} className="animate-spin" /> Customer is thinking…
            </div>
          </div>
        )}
      </div>

      {/* Response controls — one big red mic button. Auto-listen fills the
          conversational flow; the button below is the manual override for
          mobile browsers that don't reliably auto-open the mic. */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          {!recording && vadState !== "recording" ? (
            <button
              onClick={async () => {
                // Manual override — if VAD is currently listening (WebAudio
                // stream open) or if it wasn't started at all, cancel it and
                // immediately open a fresh mic stream for MediaRecorder. This
                // is the escape hatch for iOS Safari builds where VAD's
                // analyser doesn't pick up microphone input reliably.
                if (vadState === "listening") cancelVad();
                await new Promise((r) => setTimeout(r, 60));  // let the mic device release before we re-open
                startRecording();
              }}
              disabled={thinking || turnsUsed >= scenario.max_turns}
              className="btn btn-primary flex-1 justify-center !py-4 !text-base"
              data-testid="salescert-rp-record"
            >
              <Mic size={18} /> Speak your response
            </button>
          ) : (
            <button
              onClick={stopRecording}
              className="btn btn-primary flex-1 justify-center !bg-[#059669] !py-4 !text-base"
              data-testid="salescert-rp-stop"
            >
              <Square size={18} /> Stop &amp; send
            </button>
          )}
          {audioPlayerRef.current?.src && (
            <div className="relative flex items-center">
              {audioBlocked && isIosOrAndroid && (
                <span
                  className="absolute -top-6 right-0 text-[10px] font-semibold text-[#EE2737] whitespace-nowrap flex items-center gap-1 animate-pulse"
                  data-testid="salescert-rp-tap-hint"
                >
                  Tap to hear the customer ↓
                </span>
              )}
              <button
                onClick={() => {
                  // On the first mobile turn the browser has blocked autoplay
                  // (audioBlocked === true). Route the tap through the
                  // unlock path so audioUnlockedRef flips true and turns 2+
                  // autoplay. On every subsequent turn this button just
                  // replays the last customer line.
                  if (audioBlocked) {
                    forcePlayBlockedAudio();
                  } else if (audioPlayerRef.current) {
                    audioPlayerRef.current.currentTime = 0;
                    audioPlayerRef.current.play().catch(() => {});
                  }
                }}
                className={`btn btn-secondary ${audioBlocked && isIosOrAndroid ? "!bg-[#EE2737] !text-white !border-[#EE2737] animate-pulse" : ""}`}
                title="Replay the customer's last line"
                data-testid="salescert-rp-replay"
              >
                <Volume2 size={14} /> Replay
              </button>
            </div>
          )}
        </div>
        <div className="text-[10px] text-[#6B6860] text-center">
          {vadState === "listening"
            ? "The mic is already listening — start speaking, or tap the red button to record manually."
            : vadState === "recording"
              ? "Auto-recording · Sarvam will send this to the customer when you pause."
              : "The customer speaks first. When they finish, the mic opens automatically — or tap the red button any time."}
        </div>
      </div>

      <button
        onClick={() => submitSession(false)}
        className="text-xs uppercase tracking-widest text-[#878787] hover:text-[#0A0A0A]"
        data-testid="salescert-rp-end-early"
      >
        End &amp; submit early
      </button>
    </div>
    </>
  );
}
